import { formatDateTime, type Job } from '@fsm/shared';
import { Card } from './ui';

/**
 * The customer's sign-off, shown as evidence rather than as a thumbnail.
 *
 * This card exists separately from the attachments grid because of what it is
 * for. A signature is produced once and looked at when somebody disputes the
 * work, and at that moment the three things that matter are the drawing, the
 * name of whoever made it, and when. In the grid it is one tile among a dozen
 * job photos, captioned "Signature" and nothing else.
 *
 * It renders nothing at all when the job is unsigned. An empty "no signature"
 * panel on every job would be a permanent reproach on screens where no
 * signature was ever wanted — plenty of work is never signed for.
 */
export function SignOffCard({ job }: { job: Job }) {
  if (!job.signedAt) return null;

  return (
    <Card title="Sign-off" icon="check">
      <div className="space-y-3">
        {job.signatureUrl ? (
          /*
           * White, whatever the surrounding card does: these are dark strokes
           * on nothing, and on a tinted panel a signature reads as a stain.
           */
          <div className="rounded-lg border border-line bg-white p-3">
            <img
              src={job.signatureUrl}
              alt={`Signature of ${job.signedByName ?? 'the customer'}`}
              className="h-[120px] w-full object-contain"
            />
          </div>
        ) : (
          <p className="text-[13px] text-ink-4">
            The signature image is still uploading from the technician&rsquo;s phone.
          </p>
        )}

        <div className="text-[13px]">
          <div className="font-semibold text-ink">{job.signedByName ?? 'Customer'}</div>
          <div className="text-ink-3">Signed {formatDateTime(job.signedAt)}</div>
        </div>
      </div>
    </Card>
  );
}
